import { useEffect, useRef, useCallback } from 'react';
import { baseHost, isUnderBaseDomain } from '../utils/brandHost';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Main platform origins where the Google Client ID is registered
const MAIN_ORIGINS = [
  `https://${baseHost('lms')}`,
  `https://${baseHost('app')}`,
];
if (window.location.hostname === 'localhost') {
  MAIN_ORIGINS.push(`${window.location.protocol}//${window.location.host}`);
}

/**
 * Detect if the current page is on a domain where Google Sign-In can run
 * directly (i.e. an authorised JavaScript origin).
 */
function isGoogleOriginDomain() {
  const h = window.location.hostname;
  if (h === 'localhost' || h.endsWith('.localhost')) return true;
  if (isUnderBaseDomain(h)) return true;
  // Custom domain — NOT registered in Google Console
  return false;
}

/**
 * Get the main-domain URL to open the popup relay page.
 * In production this is lms.lmscampus.com; in dev it's localhost.
 */
function getPopupUrl() {
  if (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost')) {
    return `${window.location.protocol}//${window.location.hostname}:${window.location.port || 3200}`;
  }
  return `https://${baseHost('lms')}`;
}

/**
 * useGoogleAuth — handles Google Sign-In across both main-domain and custom-domain scenarios.
 *
 * On main domain (*.lmscampus.com, localhost):
 *   Returns { isDirectMode: true } → caller should render <GoogleLogin /> directly.
 *
 * On custom domain (lms.abcinstitute.com):
 *   Returns { isDirectMode: false, openPopup } → caller should render a
 *   custom button that calls openPopup().
 *
 * @param {Function} onCredential — called with the Google ID token string
 * @param {Function} onError — called on failure
 */
export default function useGoogleAuth(onCredential, onError) {
  const listenerRef = useRef(null);

  const isDirect = isGoogleOriginDomain();

  // Listen for postMessage from the popup
  useEffect(() => {
    if (isDirect) return;

    function handleMessage(event) {
      // Accept from any of our main origins
      if (!MAIN_ORIGINS.includes(event.origin) && event.origin !== getPopupUrl()) return;
      if (event.data?.type === 'google-auth-credential' && event.data.credential) {
        onCredential(event.data.credential);
      }
    }
    listenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isDirect, onCredential]);

  const openPopup = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      onError?.('Google Client ID not configured.');
      return;
    }
    const base = getPopupUrl();
    const currentOrigin = window.location.origin;
    const url = `${base}/google-auth-popup.html?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&origin=${encodeURIComponent(currentOrigin)}`;

    const w = 500;
    const h = 600;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(url, 'google-auth', `width=${w},height=${h},left=${left},top=${top},popup=1`);

    if (!popup) {
      onError?.('Popup blocked. Please allow popups for this site.');
      return;
    }

    // Poll to detect if user closed the popup without completing
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
      }
    }, 500);
  }, [onError]);

  return {
    isDirectMode: isDirect,
    openPopup,
    hasGoogleAuth: !!GOOGLE_CLIENT_ID,
  };
}
